'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Region {
  name: string;
  countries: number;
  certifications: string[];
  color: string;
}

interface GlobalPresenceMapSectionProps {
  className?: string;
}

const GlobalPresenceMapSection = ({ className = '' }: GlobalPresenceMapSectionProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const regions: Region[] = [
    {
      name: 'Europe',
      countries: 18,
      certifications: ['EU-GMP', 'ISO 9001:2015'],
      color: 'bg-primary',
    },
    {
      name: 'Asia Pacific',
      countries: 22,
      certifications: ['WHO-GMP', 'ISO 9001:2015'],
      color: 'bg-secondary',
    },
    {
      name: 'Latin America',
      countries: 12,
      certifications: ['ANVISA', 'WHO-GMP'],
      color: 'bg-accent',
    },
    {
      name: 'Middle East & Africa',
      countries: 15,
      certifications: ['WHO-GMP', 'ISO 9001:2015'],
      color: 'bg-warning',
    },
  ];

  const handleRegionClick = (regionName: string) => {
    if (!isHydrated) return;
    setSelectedRegion(selectedRegion === regionName ? null : regionName);
  };

  if (!isHydrated) {
    return (
      <section className={`py-20 bg-muted ${className}`}>
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-primary/10 rounded-full mb-4">
              <span className="text-sm font-semibold text-primary">Global Reach</span>
            </div>
            <h2 className="font-heading font-bold text-3xl lg:text-4xl text-textPrimary mb-4">
              Serving 60+ Countries Worldwide
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {regions.map((region) => (
              <div key={region.name} className="bg-card p-6 rounded-xl shadow-lg border border-border">
                <div className={`w-12 h-12 ${region.color} rounded-lg flex items-center justify-center mb-4`}>
                  <Icon name="GlobeAltIcon" size={24} className="text-white" />
                </div>
                <h3 className="font-heading font-semibold text-xl text-textPrimary mb-2">
                  {region.name}
                </h3>
                <p className="text-textSecondary mb-4">{region.countries} Countries</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={`py-20 bg-muted ${className}`}>
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <div className="inline-block px-4 py-2 bg-primary/10 rounded-full mb-4">
            <span className="text-sm font-semibold text-primary">Global Reach</span>
          </div>
          <h2 className="font-heading font-bold text-3xl lg:text-4xl text-textPrimary mb-4">
            Serving 60+ Countries Worldwide
          </h2>
          <p className="text-lg text-textSecondary max-w-3xl mx-auto">
            Our global presence spans across continents, bringing pharmaceutical excellence and regulatory expertise to diverse markets with localized support.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {regions.map((region) => (
            <button
              key={region.name}
              onClick={() => handleRegionClick(region.name)}
              className={`bg-card p-6 rounded-xl shadow-lg border-2 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 text-left ${
                selectedRegion === region.name ? 'border-primary' : 'border-border'
              }`}
            >
              <div className={`w-12 h-12 ${region.color} rounded-lg flex items-center justify-center mb-4`}>
                <Icon name="GlobeAltIcon" size={24} className="text-white" />
              </div>
              <h3 className="font-heading font-semibold text-xl text-textPrimary mb-2">
                {region.name}
              </h3>
              <p className="text-textSecondary mb-4">{region.countries} Countries</p>
              <div className="flex flex-wrap gap-2">
                {region.certifications.map((cert) => (
                  <span
                    key={cert}
                    className="px-2 py-1 bg-muted text-textSecondary text-xs font-medium rounded"
                  >
                    {cert}
                  </span>
                ))}
              </div>
            </button>
          ))}
        </div>

        {selectedRegion && (
          <div className="bg-card p-8 rounded-xl shadow-lg border border-border">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading font-bold text-2xl text-textPrimary">
                {selectedRegion} Operations
              </h3>
              <button
                onClick={() => setSelectedRegion(null)}
                className="p-2 hover:bg-muted rounded-lg transition-colors duration-300"
              >
                <Icon name="XMarkIcon" size={24} className="text-textSecondary" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="flex items-start space-x-3">
                <Icon name="BuildingOfficeIcon" size={24} className="text-primary flex-shrink-0" />
                <div>
                  <p className="font-semibold text-textPrimary mb-1">Local Partnerships</p>
                  <p className="text-sm text-textSecondary">
                    Established distribution networks with trusted local partners
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <Icon name="CheckBadgeIcon" size={24} className="text-secondary flex-shrink-0" />
                <div>
                  <p className="font-semibold text-textPrimary mb-1">Regulatory Compliance</p>
                  <p className="text-sm text-textSecondary">
                    Full compliance with regional pharmaceutical regulations
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <Icon name="TruckIcon" size={24} className="text-accent flex-shrink-0" />
                <div>
                  <p className="font-semibold text-textPrimary mb-1">Logistics Support</p>
                  <p className="text-sm text-textSecondary">
                    Efficient supply chain management and timely delivery
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="mt-12 bg-gradient-to-r from-primary/10 to-secondary/10 p-8 rounded-xl">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 text-center">
            <div>
              <p className="text-4xl font-bold text-primary mb-2">60+</p>
              <p className="text-sm text-textSecondary">Countries Served</p>
            </div>
            <div>
              <p className="text-4xl font-bold text-secondary mb-2">150+</p>
              <p className="text-sm text-textSecondary">Distribution Partners</p>
            </div>
            <div>
              <p className="text-4xl font-bold text-accent mb-2">25+</p>
              <p className="text-sm text-textSecondary">Regulatory Certifications</p>
            </div>
            <div>
              <p className="text-4xl font-bold text-warning mb-2">98%</p>
              <p className="text-sm text-textSecondary">Client Satisfaction</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GlobalPresenceMapSection;